import { DataManager } from "../model/DataManager.js";
import { VOSensor } from "../model/DataStructure.js";
import { DateUtil } from "../model/DateUtil.js";
import { Marker } from "../display/Marker.js";
import { BaseElement3d, BaseElement3dOptions } from "./BaseElement3d.js";
import { HeightMap } from "./HeightMap.js";


export interface Route3dOptions extends BaseElement3dOptions {
    radius: number;
    data: DataManager;
    date: Date;
    heightMap?: HeightMap;
}

export class Route3d extends BaseElement3d {

    private _sensors: VOSensor[] = [];
    private _geometry: THREE.BufferGeometry;
    private _marker: Marker;
    private _ripple: Marker;
    private _radius: number;

    private _timeElement: HTMLDivElement;
    private _dateElement: HTMLDivElement;
    private _data: DataManager;
    private _rangeMultiplier: number = 1;
    private _drawCount: number = 0;
    private _lookup: { [sensorId: number]: number } = {};
    private _lookupReverse: { [sensorStep: number]: VOSensor } = {};
    private _heightMap: HeightMap;

    constructor(options: Route3dOptions) {

        super();

        this._heightMap = options.heightMap;

        this.create(options);

        this._data = options.data;
        this._sensors = this._data.data.sensor.slice();

        var radius = options.radius + 0.1;
        this._radius = radius;
        var index = 0;
        var points: THREE.Vector3[] = [];
        for (var sensor of this._sensors) {
            if (sensor.longitude !== null
                && sensor.latitude !== null) {

                var r = radius;
                if (this._heightMap) {
                    r += this._heightMap.getHeight(sensor.latitude, sensor.longitude) * 6;
                }
                var v = this._canvas.lngLatToVector(sensor.longitude, sensor.latitude, r);
                points.push(v);
                this._lookup[sensor.id] = index++;
            } else {
                this._lookup[sensor.id] = index;
            }
            this._lookupReverse[this._lookup[sensor.id]] = sensor;
        }

        // var curve = new THREE.CatmullRomCurve3(points, false, "centripetal", 0.5);
        // var l = Math.round(curve.getLength() / 2);
        // var pointsBase = curve.getPoints(l);
        // for (var i = 0; i <= l; ++i) {
        //     var pp = curve.getPoint(i / l);
        //     pp.normalize();
        //     pp.multiplyScalar(radius);
        //     points.push(pp);
        // }
        // points = pointsBase;

        this._marker = new Marker(this._canvas, {
            lat: 0,
            lng: 0,
            originX: "100%",
            originY: "50%",
            offsetX: -25,
            zIndexOffset: 64
        });

        var div = document.createElement("div");
        div.className = "awi-route__marker";
        div.innerHTML = `
        <div class="awi-route__marker-triangle"></div>
        <div class="awi-route__marker-time"></div>
        <div class="awi-route__marker-date"></div>
        `;
        this._marker.element.appendChild(div);
        this._timeElement = div.querySelector(".awi-route__marker-time");
        this._dateElement = div.querySelector(".awi-route__marker-date");
        this._canvas.addMarker(this._marker);

        this._ripple = new Marker(this._canvas, {
            zIndexOffset: -200
        });
        var ripple = document.createElement("div");
        ripple.className = "awi-route__ripple";
        this._ripple.element.appendChild(ripple);
        this._canvas.addMarker(this._ripple);

        // this.addGlLine(points);
        this.addMeshLine(points);

        this.setDate(options.date, 0);
    }

    public setDate(date: Date, time: number | "auto" = "auto"): VOSensor {
        var day = this._data.getDay(date);
        if (!day) {
            return null;
        }

        var sensor = day.position;
        // if (!sensor) {
        //     return;
        // }

        if (!sensor) {
            this._marker.element.remove();
            this._ripple.element.remove();
            return;
        }

        var date = sensor.date;

        // lang.set(this._timeElement, {
        //     de: DateUtil.toTimeString(date, "de"),
        //     en: DateUtil.toTimeString(date, "en")
        // });
        // lang.set(this._dateElement, {
        //     de: DateUtil.toStringReadableShortLocal(date, "de"),
        //     en: DateUtil.toStringReadableShortLocal(date, "en")
        // });

        lang.set(this._timeElement, {
            de: DateUtil.toTimeStringUTC(date, "de"),
            en: DateUtil.toTimeStringUTC(date, "en")
        });
        lang.set(this._dateElement, {
            de: DateUtil.toStringReadableShort(date, "de") + " UTC",
            en: DateUtil.toStringReadableShort(date, "en") + " UTC"
        });

        this.setDrawCount(this._lookup[sensor.id] + 1, time);
        return sensor;
    }

    private setDrawCount(count: number, time: number | "auto" = "auto"): void {
        var duration = 0;
        if (time === "auto") {
            duration = 1;
        } else {
            duration = time;
        }
        gsap.to(this, { duration: duration, drawCount: count });
    }

    private addGlLine(points: THREE.Vector3[]): void {
        this._geometry = new THREE.BufferGeometry().setFromPoints(points);
        const material = new THREE.LineBasicMaterial({ color: 0x17ABE8, linewidth: 1 });
        this.addMaterial(material);
        const line = new THREE.Line(this._geometry, material);
        this._geometry.setDrawRange(0, 0);
        this.element.add(line);
    }

    private addMeshLine(points: THREE.Vector3[]): void {
        const line = new MeshLine.MeshLine();
        line.setPoints(points);
        const material = new MeshLine.MeshLineMaterial({
            color: new THREE.Color("#17ABE8"),
            sizeAttenuation: 0,
            lineWidth: 0.003 * 1.25
        });
        this.addMaterial(material);
        const mesh = new THREE.Mesh(line, material);
        this.element.add(mesh);
        this._geometry = line;
        this._geometry.setDrawRange(0, 0);
        this._rangeMultiplier = 6;
    }

    private get drawCount(): number {
        return this._drawCount;
    }

    private set drawCount(value: number) {
        value = e5.Calc.clamp(Math.round(value), 1, this._sensors.length);
        if (this._drawCount === value) {
            return;
        }
        this._drawCount = value;
        this._geometry.setDrawRange(0, value * this._rangeMultiplier);

        var sensor = this._lookupReverse[value - 1];
        if (sensor) {
            this._canvas.lngLatToVector(sensor.longitude, sensor.latitude, this._radius, this._marker.position);
            this._ripple.position.copy(this._marker.position);
        } else {
            console.warn("Could not find sensor for drawCount index");
        }
    }
}